import { dismissToasts } from '@pypestream/design-system';

import { tableStore } from '../stores/table-store';
import { sendUserMessage } from '../xstate/app.xstate-utils';

export const PUBLISH_IN_PROGRESS_TOAST_ID = 'publish-in-progress';

/** Status values for solution version (from backend). */
export type SolutionVersionStatus = 'ACTIVE' | 'PUBLISHING' | 'CREATING';

/** Version ids we've already run "publish complete" for (dismiss + edit + success toast). */
const handledPublishCompleteVersionIds = new Set<string>();

/** Versions whose most recent publish attempt FAILED — suppress success toasts for these. */
const failedPublishVersionIds = new Set<string>();

/**
 * Restores UI state after a publish attempt resolves (in-progress toast gone,
 * editing mode back). Idempotent — safe to call from multiple subscribers.
 */
function restorePublishUiState(): void {
  tableStore.setSolutionVersionPublishingInProgress(false);
  dismissToasts(PUBLISH_IN_PROGRESS_TOAST_ID);
  // PSTUDIO-3985: `isViewer`-aware, like every other restore site in
  // `table-store.ts`. A project VIEWER sitting on a solution a coworker
  // publishes reaches this on PUBLISHING -> ACTIVE, and an unconditional
  // `'editing'` handed them an editable table.
  tableStore.setState(tableStore.isViewer ? 'viewing' : 'editing');
}

/**
 * Call when a publish for this version STARTS. Resets prior failed/handled state
 * so a subsequent genuine publish of the same version id still reports success.
 */
export function markPublishStarted(versionId: string): void {
  failedPublishVersionIds.delete(versionId);
  handledPublishCompleteVersionIds.delete(versionId);
}

/** Call when the publish mutation for this version FAILED. */
export function markPublishFailed(versionId: string): void {
  failedPublishVersionIds.add(versionId);
}

/** Whether the last publish attempt for this version failed. */
export function isPublishFailed(versionId: string): boolean {
  return failedPublishVersionIds.has(versionId);
}

/**
 * Dismisses the publish-in-progress toast, switches to edit mode, and shows success toast.
 * Only announces once per versionId per session — the subscription replays the current row on
 * every (re)subscribe, so the same ACTIVE can arrive more than once for one publish.
 * Never shows a success toast for a version whose publish attempt failed — the status
 * field can revert to ACTIVE on failure (there is no FAILED status), so the registry is
 * the authoritative guard.
 */
export function notifyPublishComplete(
  versionId: string,
  successMessage: string
): void {
  // Always restore UI state — even on a failed version the user must not stay
  // stuck in viewing mode with a lingering in-progress toast.
  restorePublishUiState();
  if (failedPublishVersionIds.has(versionId)) {
    return;
  }
  // Only show the success toast once per version (two subscribers can race).
  if (handledPublishCompleteVersionIds.has(versionId)) {
    return;
  }
  handledPublishCompleteVersionIds.add(versionId);
  sendUserMessage({
    type: 'success',
    text: successMessage,
    autoClose: 5000,
  });
}
